/**
 * CustomizeCardSheet (web): the "Edit share preview" view of the Share dialog
 * (design repo popout-share, 2026-10-05). The dialog header and the live card
 * stay pinned; only the form scrolls, under soft mask-image fades. Card theme
 * grid (Pro marks on paid themes), custom colour (picker + hex), background
 * silhouette grid, profile photo, name, headline, button text with presets,
 * card link, and our Pro extras (background image with crop, hide the Tabbio
 * logo). Footer: "Save preview", or for a Free draft holding paid choices
 * "Upgrade to save" + "Restore free options".
 *
 * Preview-first: the card renders the local draft and nothing is written
 * until Save (`updateCVConfig`; server coercion stays authoritative). The
 * draft is kept per CV (and mirrored to sessionStorage) so it survives the
 * paywall round trip. Save hands control back to the dialog through `onDone`.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { ProIcon } from '../icons/ProIcon';
import { useTranslation } from 'react-i18next';
import type { ShareCardConfig, ShareCardPattern } from '@tabbio/types';
import {
  getShareCardTheme,
  shareCardThemeId,
  type ShareCardThemeId,
} from '@tabbio/utils/cv-share-card';
import { SHARE_CARD_ORBIT_ART } from '@tabbio/utils/share-card-art';
import { TABBIO_MARK_PATH } from '../common/TabbioMark';
import { useCVData } from '../../lib/cv-mock-data';
import { useEntitlement } from '../../providers/SubscriptionProvider.shared';
import { useUpgradeModal } from '../../providers/UpgradeModalProvider';
import {
  fromWebFile,
  uploadImageAsset,
  type UploadableImageAsset,
} from '../../lib/image-upload';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { useToast } from '../../hooks/useToast';
import { useLocale } from '../../hooks/useLocale';
import { useTheme } from '../../hooks/useTheme';
import { ShareCardPreview } from './ShareCardPreview.web';
import { buildPublicCvRouteUrl } from '../../lib/public-web';
import { SHARE_SHEET_CHOICE, SHARE_SHEET_COLORS } from './share-sheet-palette';
import { shareThemeArtUri } from './share-theme-art';
import {
  clearShareCardDraft,
  readShareCardDraft,
  shareCardDraftKey,
  writeShareCardDraft,
} from './share-card-draft';
import {
  SHARE_CARD_CTA_INPUT_MAX,
  SHARE_CARD_CTA_PRESETS,
  SHARE_CARD_EDITOR_PATTERNS,
  SHARE_CARD_EDITOR_THEME_IDS,
  SHARE_CARD_HEADLINE_INPUT_MAX,
  SHARE_CARD_NAME_INPUT_MAX,
  SHARE_CARD_PATTERN_SWATCH_ART,
  applyShareCardCta,
  applyShareCardCustomColor,
  applyShareCardHeadline,
  applyShareCardLinkInput,
  applyShareCardTitle,
  clearShareCardLink,
  initialShareCardDraft,
  isPaidShareCardTheme,
  isShareCardLinkChanged,
  parseShareCardHexInput,
  resetShareCardLink,
  restoreFreeShareCard,
  selectShareCardPattern,
  selectShareCardTheme,
  shareCardCtaFieldValue,
  shareCardCustomHex,
  shareCardLinkFieldValue,
  shareCardSaveGate,
  shareCardSelectedCtaPreset,
  shareCardThemeSwatchCss,
} from './share-card-editor';

/** Design: the form fades out over 14px at the top and 12px at the bottom. */
const FADE_TOP = 14;
const FADE_BOTTOM = 12;

export interface CustomizeCardSheetProps {
  publicUrl?: string | null;
  /** Save returns control to the owning dialog (back to its main view). */
  onDone?: () => void;
}

export function CustomizeCardSheet({ publicUrl, onDone }: CustomizeCardSheetProps = {}) {
  const { t } = useTranslation(['seeker', 'common']);
  const { isRTL } = useLocale();
  const { isDark } = useTheme();
  const choice: ChoiceColors = {
    background: isDark ? SHARE_SHEET_CHOICE.darkBackground : SHARE_SHEET_CHOICE.background,
    text: isDark ? SHARE_SHEET_CHOICE.darkText : SHARE_SHEET_CHOICE.text,
  };
  const { cvConfig, profile, updateCVConfig, flushCVConfigSave, target } = useCVData();
  const canBrand = useEntitlement('individual.custom_branding').allowed;
  const upgrade = useUpgradeModal();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  // The card's default link: the owner's public CV.
  const cardPublicUrl =
    publicUrl ?? (profile.username ? buildPublicCvRouteUrl(profile.username) : null);

  const draftKey = shareCardDraftKey(target);
  const [draft, setDraft] = useState<ShareCardConfig>(
    () => readShareCardDraft(draftKey) ?? initialShareCardDraft(cvConfig.card),
  );
  // Text fields keep their own strings so partial input (a half-typed URL or
  // hex) can sit in the box without reaching the card until it is valid.
  const [lastHex, setLastHex] = useState(() => shareCardCustomHex(draft));
  const [hexText, setHexText] = useState(lastHex);
  const [linkText, setLinkText] = useState(() => shareCardLinkFieldValue(draft, cardPublicUrl));
  const [ctaText, setCtaText] = useState(() => shareCardCtaFieldValue(draft));
  const [uploading, setUploading] = useState(false);
  const [pendingCrop, setPendingCrop] = useState<UploadableImageAsset | null>(null);
  const [cropZoom, setCropZoom] = useState(1);
  const [cropX, setCropX] = useState(50);
  const [cropY, setCropY] = useState(50);

  useEffect(() => {
    const uri = pendingCrop?.uri;
    return () => {
      if (uri) URL.revokeObjectURL(uri);
    };
  }, [pendingCrop?.uri]);

  const setCard = (next: ShareCardConfig) => {
    setDraft(next);
    writeShareCardDraft(draftKey, next);
  };

  const syncFields = (card: ShareCardConfig) => {
    const hex = shareCardCustomHex(card);
    setLastHex(hex);
    setHexText(hex);
    setLinkText(shareCardLinkFieldValue(card, cardPublicUrl));
    setCtaText(shareCardCtaFieldValue(card));
  };

  const gate = shareCardSaveGate(draft, canBrand);
  const selectedTheme = shareCardThemeId(draft.background);
  const selectedPreset = shareCardSelectedCtaPreset(draft);

  const pickTheme = (id: ShareCardThemeId) => setCard(selectShareCardTheme(draft, id, lastHex));

  const applyHex = (text: string) => {
    setHexText(text);
    const parsed = parseShareCardHexInput(text);
    if (!parsed) return;
    setLastHex(parsed);
    setCard(applyShareCardCustomColor(draft, parsed));
  };

  const onCtaChange = (text: string) => {
    setCtaText(text);
    setCard(applyShareCardCta(draft, text));
  };

  const pickCtaPreset = (label: string) => {
    setCtaText(label);
    setCard(applyShareCardCta(draft, label));
  };

  const onLinkChange = (text: string) => {
    setLinkText(text);
    setCard(applyShareCardLinkInput(draft, text, cardPublicUrl));
  };

  const resetLink = () => {
    const next = resetShareCardLink(draft);
    setCard(next);
    setLinkText(shareCardLinkFieldValue(next, cardPublicUrl));
  };

  const clearLink = () => {
    setCard(clearShareCardLink(draft));
    setLinkText('');
  };

  const restoreFree = () => {
    const next = restoreFreeShareCard(draft);
    setCard(next);
    syncFields(next);
  };

  const requestUpgrade = () => {
    // The draft is already in the store (and sessionStorage), so it is back
    // when the paywall closes or the page reloads.
    upgrade.open('individual');
  };

  const save = () => {
    if (gate.mode === 'upgrade') {
      requestUpgrade();
      return;
    }
    updateCVConfig({ card: draft });
    flushCVConfigSave();
    clearShareCardDraft(draftKey);
    toast.show({ title: t('cv.customizeCard.saved'), type: 'success' });
    onDone?.();
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const asset = fromWebFile(file);
      setCropZoom(1);
      setCropX(50);
      setCropY(50);
      setPendingCrop((previous) => {
        if (previous?.uri) URL.revokeObjectURL(previous.uri);
        return asset;
      });
    } catch {
      toast.show({ title: t('cv.customizeCard.uploadFailed'), type: 'error' });
    }
  };

  const cancelCrop = () => {
    setPendingCrop((previous) => {
      if (previous?.uri) URL.revokeObjectURL(previous.uri);
      return null;
    });
  };

  const applyCrop = async () => {
    if (!pendingCrop) return;
    try {
      setUploading(true);
      const blob = await cropShareCardImage(pendingCrop.uri, {
        zoom: cropZoom,
        x: cropX,
        y: cropY,
      });
      const uploadName = pendingCrop.name.replace(/\.[^.]+$/u, '') + '-card.jpg';
      const url = await uploadImageAsset(
        {
          uri: pendingCrop.uri,
          name: uploadName,
          type: blob.type,
          size: blob.size,
          file: blob,
        },
        {
          kind: 'cv-share-card-bg',
          fileName: uploadName,
          mimeType: blob.type,
        },
      );
      setCard({ ...draft, backgroundImage: url });
      cancelCrop();
    } catch {
      toast.show({ title: t('cv.customizeCard.uploadFailed'), type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const proMark = <ProIcon size={12} boxed={false} color="currentColor" />;
  const paidLabel = (paid: boolean, label: string) =>
    paid ? `${label} · ${t('cv.customizeCard.premium')}` : label;

  return (
    <div style={root}>
      {/* Pinned: the live card renders the local draft. The CTA is artwork here. */}
      <ShareCardPreview publicUrl={cardPublicUrl} card={draft} />

      <form
        style={form}
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        {!canBrand ? <p style={note}>{t('cv.customizeCard.trialNote')}</p> : null}

        {/* Card theme */}
        <fieldset style={fieldset}>
          <legend style={legend}>{t('cv.customizeCard.theme')}</legend>
          <div style={themeGrid}>
            {SHARE_CARD_EDITOR_THEME_IDS.map((id) => {
              const selected = selectedTheme === id;
              const paid = !canBrand && isPaidShareCardTheme(id);
              const label = t(`cv.customizeCard.themes.${id}`);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => pickTheme(id)}
                  aria-pressed={selected}
                  aria-label={paidLabel(paid, label)}
                  style={tile(selected, choice)}
                >
                  <ThemeSwatch id={id} customHex={lastHex} />
                  <span style={tileLabel}>{label}</span>
                  {paid ? proMark : null}
                </button>
              );
            })}
          </div>
          {selectedTheme === 'custom' ? (
            <div style={customBlock}>
              <label htmlFor="share-card-color-hex" style={label}>
                {t('cv.customizeCard.customColor')}
              </label>
              <div style={customRow}>
                <input
                  type="color"
                  value={lastHex}
                  onChange={(event) => applyHex(event.currentTarget.value)}
                  aria-label={t('cv.customizeCard.customColor')}
                  style={colorInput}
                />
                <input
                  id="share-card-color-hex"
                  value={hexText}
                  onChange={(event) => applyHex(event.currentTarget.value)}
                  aria-label={t('cv.customizeCard.hexColor')}
                  maxLength={7}
                  spellCheck={false}
                  autoComplete="off"
                  style={{ ...input, ...hexInput }}
                />
              </div>
            </div>
          ) : null}
        </fieldset>

        {/* Background silhouette */}
        <fieldset style={fieldset}>
          <legend style={legend}>{t('cv.customizeCard.background')}</legend>
          <div style={patternGrid}>
            {SHARE_CARD_EDITOR_PATTERNS.map((pattern) => {
              const selected = (draft.pattern ?? 'signature') === pattern;
              const paid = !canBrand && pattern !== 'none' && pattern !== 'signature';
              const label = t(`cv.customizeCard.patterns.${pattern}`);
              return (
                <button
                  key={pattern}
                  type="button"
                  onClick={() => setCard(selectShareCardPattern(draft, pattern))}
                  aria-pressed={selected}
                  aria-label={paidLabel(paid, label)}
                  style={{ ...tile(selected, choice), flexDirection: 'column', minHeight: 60 }}
                >
                  <PatternSwatch pattern={pattern} />
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <span style={tileLabel}>{label}</span>
                    {paid ? proMark : null}
                  </span>
                </button>
              );
            })}
          </div>
        </fieldset>

        {/* Use profile photo */}
        <div style={toggleRow}>
          <span style={rowTitle}>{t('cv.customizeCard.usePhoto')}</span>
          <Toggle
            value={draft.showPhoto}
            onChange={(next) => setCard({ ...draft, showPhoto: next })}
            ariaLabel={t('cv.customizeCard.usePhoto')}
          />
        </div>

        {/* Name */}
        <label htmlFor="share-card-name" style={label}>
          {t('cv.customizeCard.name')}
        </label>
        <input
          id="share-card-name"
          value={draft.title ?? ''}
          onChange={(event) => setCard(applyShareCardTitle(draft, event.currentTarget.value))}
          placeholder={profile.name || t('cv.customizeCard.name')}
          maxLength={SHARE_CARD_NAME_INPUT_MAX}
          autoComplete="off"
          dir="auto"
          style={input}
        />

        {/* Headline */}
        <label htmlFor="share-card-headline" style={label}>
          {t('cv.customizeCard.headline')}
        </label>
        <input
          id="share-card-headline"
          value={draft.description ?? ''}
          onChange={(event) => setCard(applyShareCardHeadline(draft, event.currentTarget.value))}
          placeholder={profile.headline || t('cv.customizeCard.headline')}
          maxLength={SHARE_CARD_HEADLINE_INPUT_MAX}
          autoComplete="off"
          dir="auto"
          style={input}
        />

        {/* Button text + presets */}
        <label htmlFor="share-card-cta" style={{ ...label, ...labelWithMark }}>
          {t('cv.customizeCard.buttonText')}
          {!canBrand ? proMark : null}
        </label>
        <input
          id="share-card-cta"
          value={ctaText}
          onChange={(event) => onCtaChange(event.currentTarget.value)}
          maxLength={SHARE_CARD_CTA_INPUT_MAX}
          autoComplete="off"
          dir="auto"
          style={{ ...input, marginBottom: 8 }}
        />
        <div role="group" aria-label={t('cv.customizeCard.buttonPresets')} style={chipRow}>
          {SHARE_CARD_CTA_PRESETS.map((preset) => {
            const selected = selectedPreset === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => pickCtaPreset(preset.label)}
                aria-pressed={selected}
                style={chip(selected, choice)}
              >
                {preset.id === 'view' ? t('cv.share.cardCta') : t('cv.share.downloadCv')}
              </button>
            );
          })}
        </div>

        {/* Card link */}
        <div style={linkHeading}>
          <label htmlFor="share-card-url" style={{ ...label, ...labelWithMark, marginBottom: 0 }}>
            {t('cv.customizeCard.cardLink')}
            {!canBrand ? proMark : null}
          </label>
          {isShareCardLinkChanged(draft) ? (
            <button type="button" onClick={resetLink} style={{ ...textButton, color: choice.text }}>
              {t('cv.customizeCard.resetLink')}
            </button>
          ) : null}
        </div>
        <div style={linkControl}>
          {draft.cardUrl === undefined || draft.cardUrl ? (
            <svg aria-hidden viewBox="0 0 24.0361 27.9998" width={12} height={14} style={{ flexShrink: 0 }}>
              <path d={TABBIO_MARK_PATH} fill="var(--text-tertiary)" />
            </svg>
          ) : null}
          <input
            id="share-card-url"
            type="url"
            value={linkText}
            onChange={(event) => onLinkChange(event.currentTarget.value)}
            placeholder="https://"
            inputMode="url"
            autoCapitalize="off"
            spellCheck={false}
            autoComplete="off"
            dir="ltr"
            style={{ ...input, ...linkInput }}
          />
          {linkText.length ? (
            <button
              type="button"
              onClick={clearLink}
              aria-label={t('cv.customizeCard.clearLink')}
              style={{ ...textButton, color: 'var(--text-secondary)' }}
            >
              {t('cv.customizeCard.clearLink')}
            </button>
          ) : null}
        </div>
        <p style={note}>{t('cv.customizeCard.linkNote')}</p>

        <div style={divider} />

        {/* Pro extras: hide the mark, photo background */}
        <div style={toggleRow}>
          <span style={{ ...rowTitle, ...labelWithMark }}>
            {t('cv.customizeCard.hideLogo')}
            {!canBrand ? proMark : null}
          </span>
          <Toggle
            value={draft.hideBranding}
            onChange={(next) => setCard({ ...draft, hideBranding: next })}
            ariaLabel={t('cv.customizeCard.hideLogo')}
          />
        </div>

        <div style={toggleRow}>
          <button
            type="button"
            // An upload cannot be "tried": it is stored the moment it is picked, so
            // the one Pro option that is not preview-first goes straight to the paywall.
            onClick={() => (canBrand ? fileRef.current?.click() : requestUpgrade())}
            style={rowBtn}
          >
            <span style={{ ...rowTitle, ...labelWithMark }}>
              {t('cv.customizeCard.addBackgroundImage')}
              {!canBrand ? proMark : null}
            </span>
            {uploading ? (
              <span style={{ ...note, margin: 0 }}>{t('cv.customizeCard.uploading')}</span>
            ) : draft.backgroundImage ? null : (
              <MaterialSymbol
                name="chevron_right"
                size={18}
                style={{ color: 'var(--text-tertiary)', transform: isRTL ? 'scaleX(-1)' : undefined }}
              />
            )}
          </button>
          {draft.backgroundImage && !uploading ? (
            <button
              type="button"
              aria-label={t('cv.customizeCard.removeImage')}
              onClick={() => setCard({ ...draft, backgroundImage: null })}
              style={iconBtn}
            >
              <MaterialSymbol name="delete" size={16} />
            </button>
          ) : null}
        </div>
        <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} style={{ display: 'none' }} />

        {pendingCrop ? (
          <div style={cropPanel}>
            <div style={cropFrame}>
              <img
                src={pendingCrop.uri}
                alt=""
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: `${cropX}% ${cropY}%`,
                  transform: `scale(${cropZoom})`,
                  transformOrigin: `${cropX}% ${cropY}%`,
                }}
              />
            </div>
            <div style={cropControlRow}>
              <MaterialSymbol name="zoom_out" size={18} style={{ color: 'var(--text-tertiary)' }} />
              <input
                type="range"
                min={1}
                max={2.5}
                step={0.05}
                value={cropZoom}
                onChange={(event) => setCropZoom(Number(event.currentTarget.value))}
                aria-label={t('common:settings.memoryGraph.zoomIn')}
                style={cropRange}
              />
              <MaterialSymbol name="zoom_in" size={18} style={{ color: 'var(--text-tertiary)' }} />
            </div>
            <div style={cropControlRow}>
              <MaterialSymbol name="swap_horiz" size={18} style={{ color: 'var(--text-tertiary)' }} />
              <input
                type="range"
                min={0}
                max={100}
                value={cropX}
                onChange={(event) => setCropX(Number(event.currentTarget.value))}
                aria-label={t('cv.customizeCard.background')}
                style={cropRange}
              />
              <MaterialSymbol name="swap_vert" size={18} style={{ color: 'var(--text-tertiary)' }} />
              <input
                type="range"
                min={0}
                max={100}
                value={cropY}
                onChange={(event) => setCropY(Number(event.currentTarget.value))}
                aria-label={t('cv.customizeCard.background')}
                style={cropRange}
              />
            </div>
            <div style={cropActions}>
              <button type="button" onClick={cancelCrop} style={secondaryBtn}>
                {t('cv.edit.cancel')}
              </button>
              <button type="button" onClick={() => void applyCrop()} disabled={uploading} style={smallPrimaryBtn}>
                {uploading ? t('cv.customizeCard.uploading') : t('cv.customizeCard.done')}
              </button>
            </div>
          </div>
        ) : null}

        {/* Footer: Save, or the Free user's upgrade + restore pair */}
        <button type="submit" data-requires-premium={gate.mode === 'upgrade'} style={primaryBtn}>
          {gate.mode === 'upgrade' ? <ProIcon size={14} boxed={false} color="#ffffff" /> : null}
          {gate.mode === 'upgrade'
            ? t('cv.customizeCard.upgradeToSave')
            : t('cv.customizeCard.savePreview')}
        </button>
        {gate.canRestoreFree ? (
          <button type="button" onClick={restoreFree} style={{ ...restoreBtn, color: choice.text }}>
            {t('cv.customizeCard.restoreFree')}
          </button>
        ) : null}
      </form>
    </div>
  );
}

async function cropShareCardImage(
  src: string,
  crop: { zoom: number; x: number; y: number },
) {
  const image = await loadImage(src);
  const canvas = document.createElement('canvas');
  canvas.width = 1146;
  canvas.height = 600;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Could not prepare image crop');
  }

  const coverScale =
    Math.max(canvas.width / image.naturalWidth, canvas.height / image.naturalHeight) *
    crop.zoom;
  const drawWidth = image.naturalWidth * coverScale;
  const drawHeight = image.naturalHeight * coverScale;
  const drawX = (canvas.width - drawWidth) * (crop.x / 100);
  const drawY = (canvas.height - drawHeight) * (crop.y / 100);

  context.drawImage(image, drawX, drawY, drawWidth, drawHeight);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }
        reject(new Error('Could not export image crop'));
      },
      'image/jpeg',
      0.9,
    );
  });
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

/** The 12px disc on a theme tile: the rendered art, the design gradient or solid, or the custom rainbow. */
function ThemeSwatch({ id, customHex }: { id: ShareCardThemeId; customHex: string }) {
  const theme = id === 'custom' ? null : getShareCardTheme(id);
  const background: CSSProperties = theme?.artKey
    ? { backgroundImage: `url(${shareThemeArtUri(theme.artKey)})`, backgroundSize: 'cover' }
    : { background: shareCardThemeSwatchCss(id) };
  return (
    <i aria-hidden style={{ ...swatch, ...background }}>
      {id === 'custom' ? <span style={{ ...swatchCore, background: customHex }} /> : null}
    </i>
  );
}

/** The 32x19 miniature on a silhouette tile (design `.pattern-swatch`). */
function PatternSwatch({ pattern }: { pattern: ShareCardPattern }) {
  if (pattern === 'orbit') {
    return (
      <span aria-hidden style={patternBox}>
        <svg width={32} height={19} viewBox={SHARE_CARD_ORBIT_ART.viewBox} preserveAspectRatio="none">
          <path d={SHARE_CARD_ORBIT_ART.path} fill="#c1b7df" />
        </svg>
      </span>
    );
  }
  if (pattern === 'none') return <span aria-hidden style={patternBox} />;
  const { art, size } = SHARE_CARD_PATTERN_SWATCH_ART[pattern];
  return (
    <span aria-hidden style={{ ...patternBox, display: 'inline-grid', placeItems: 'center' }}>
      <svg width={size} height={size} viewBox={art.viewBox}>
        <path d={art.path} fill={SHARE_SHEET_CHOICE.border} />
      </svg>
    </span>
  );
}

function Toggle({
  value,
  onChange,
  ariaLabel,
}: {
  value: boolean;
  onChange: (next: boolean) => void;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      aria-label={ariaLabel}
      onClick={() => onChange(!value)}
      style={{
        position: 'relative',
        width: 44,
        height: 26,
        borderRadius: 9999,
        border: 0,
        cursor: 'pointer',
        flexShrink: 0,
        background: value ? 'var(--text-primary)' : 'var(--hover-strong, rgba(0,0,0,0.1))',
        transition: 'background-color 150ms',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          insetInlineStart: value ? 20 : 2,
          width: 22,
          height: 22,
          borderRadius: 11,
          background: 'var(--bg)',
          transition: 'inset-inline-start 150ms',
        }}
      />
    </button>
  );
}

const root: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  gap: 10,
  fontFamily: "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
  color: 'var(--text-primary)',
};
// Only the form scrolls; the fades are a mask so they never catch the pointer.
const form: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  margin: 0,
  padding: `${FADE_TOP}px 2px ${FADE_BOTTOM + 6}px`,
  scrollPaddingTop: FADE_TOP,
  scrollPaddingBottom: FADE_BOTTOM,
  scrollbarWidth: 'none',
  maskImage: `linear-gradient(to bottom, transparent 0, #000 ${FADE_TOP}px, #000 calc(100% - ${FADE_BOTTOM}px), transparent 100%)`,
  WebkitMaskImage: `linear-gradient(to bottom, transparent 0, #000 ${FADE_TOP}px, #000 calc(100% - ${FADE_BOTTOM}px), transparent 100%)`,
};
const note: CSSProperties = {
  margin: '0 0 16px',
  fontSize: 12,
  lineHeight: 1.6,
  color: 'var(--text-secondary)',
};
const fieldset: CSSProperties = { border: 0, padding: 0, margin: '0 0 20px', minWidth: 0 };
const legend: CSSProperties = { fontSize: 13, fontWeight: 600, marginBottom: 9, padding: 0 };
const label: CSSProperties = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 7 };
const labelWithMark: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, color: 'inherit' };
const themeGrid: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 6 };
const patternGrid: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 };
interface ChoiceColors {
  background: string;
  text: string;
}
const tile = (selected: boolean, choice: ChoiceColors): CSSProperties => ({
  minHeight: 44,
  minWidth: 0,
  border: `1px solid ${selected ? SHARE_SHEET_CHOICE.border : 'var(--input-border)'}`,
  background: selected ? choice.background : 'var(--input-bg)',
  color: selected ? choice.text : 'var(--text-primary)',
  borderRadius: 10,
  fontSize: 11,
  fontFamily: 'inherit',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 5,
  padding: 4,
  cursor: 'pointer',
});
const tileLabel: CSSProperties = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const swatch: CSSProperties = {
  position: 'relative',
  display: 'inline-block',
  width: 12,
  height: 12,
  borderRadius: '50%',
  border: '1px solid #dddddd',
  boxSizing: 'border-box',
  flexShrink: 0,
  overflow: 'hidden',
};
const swatchCore: CSSProperties = { position: 'absolute', inset: 2, borderRadius: '50%' };
const patternBox: CSSProperties = {
  display: 'inline-block',
  width: 32,
  height: 19,
  borderRadius: 4,
  background: 'var(--hover, #f2f1f5)',
  overflow: 'hidden',
};
const customBlock: CSSProperties = { marginTop: 14 };
const customRow: CSSProperties = { display: 'flex', gap: 10, alignItems: 'center' };
const colorInput: CSSProperties = {
  appearance: 'none',
  WebkitAppearance: 'none',
  width: 44,
  minWidth: 44,
  height: 44,
  border: '1px solid var(--input-border)',
  borderRadius: 9,
  padding: 3,
  margin: 0,
  cursor: 'pointer',
  background: 'var(--input-bg)',
};
const input: CSSProperties = {
  width: '100%',
  minHeight: 48,
  borderRadius: 11,
  border: '1px solid var(--input-border)',
  padding: '10px 12px',
  marginBottom: 16,
  fontSize: 15,
  color: 'var(--text-primary)',
  background: 'var(--input-bg)',
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};
const hexInput: CSSProperties = { flex: 1, minWidth: 0, margin: 0, fontFamily: 'ui-monospace, monospace', textTransform: 'uppercase' };
const toggleRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  minHeight: 44,
  marginBottom: 14,
};
const rowTitle: CSSProperties = { fontSize: 14, fontWeight: 600, letterSpacing: -0.14 };
const rowBtn: CSSProperties = {
  ...toggleRow,
  flex: 1,
  minWidth: 0,
  marginBottom: 0,
  border: 0,
  background: 'transparent',
  cursor: 'pointer',
  fontFamily: 'inherit',
  color: 'var(--text-primary)',
  padding: 0,
  textAlign: 'start',
};
const iconBtn: CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 8,
  border: 0,
  cursor: 'pointer',
  background: 'transparent',
  color: 'var(--text-tertiary)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};
const chipRow: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18 };
const chip = (selected: boolean, choice: ChoiceColors): CSSProperties => ({
  minHeight: 34,
  padding: '0 12px',
  borderRadius: 999,
  border: `1px solid ${selected ? SHARE_SHEET_CHOICE.border : 'var(--input-border)'}`,
  background: selected ? choice.background : 'var(--input-bg)',
  color: selected ? choice.text : 'var(--text-primary)',
  fontSize: 12,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
});
const linkHeading: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  minHeight: 36,
};
const textButton: CSSProperties = {
  border: 0,
  background: 'transparent',
  color: SHARE_SHEET_CHOICE.text,
  fontSize: 12,
  fontWeight: 600,
  fontFamily: 'inherit',
  minHeight: 36,
  padding: 0,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const linkControl: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  border: '1px solid var(--input-border)',
  borderRadius: 12,
  padding: '0 10px',
  marginBottom: 10,
  minWidth: 0,
  background: 'var(--input-bg)',
};
const linkInput: CSSProperties = { border: 0, margin: 0, minWidth: 0, flex: 1, paddingInline: 0, background: 'transparent' };
const divider: CSSProperties = { height: 1, background: 'var(--hairline, rgba(0,0,0,0.06))', margin: '2px 0 14px' };
const cropPanel: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: 12,
  marginBottom: 14,
  borderRadius: 18,
  background: 'rgba(0,0,0,0.04)',
};
const cropFrame: CSSProperties = {
  position: 'relative',
  width: '100%',
  aspectRatio: '1.91 / 1',
  borderRadius: 14,
  overflow: 'hidden',
  background: 'rgba(0,0,0,0.06)',
};
const cropControlRow: CSSProperties = { display: 'flex', alignItems: 'center', gap: 10 };
const cropRange: CSSProperties = { flex: 1, minWidth: 0 };
const cropActions: CSSProperties = { display: 'flex', gap: 8, justifyContent: 'flex-end' };
const secondaryBtn: CSSProperties = {
  height: 38,
  borderRadius: 999,
  border: '1px solid var(--input-border)',
  background: 'transparent',
  color: 'var(--text-primary)',
  paddingInline: 16,
  fontSize: 14,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
};
const smallPrimaryBtn: CSSProperties = {
  height: 38,
  borderRadius: 999,
  border: 0,
  background: 'var(--text-primary)',
  color: 'var(--bg)',
  paddingInline: 18,
  fontSize: 14,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
};
const primaryBtn: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  width: '100%',
  minHeight: 50,
  marginTop: 4,
  borderRadius: 13,
  border: 0,
  background: SHARE_SHEET_COLORS.primary,
  color: '#ffffff',
  fontSize: 15,
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
};
const restoreBtn: CSSProperties = {
  ...textButton,
  width: '100%',
  minHeight: 44,
  fontSize: 13,
  fontWeight: 500,
};
